import React, { useState } from 'react';
import { X, UserPlus, User, Mail, Phone, Cake, FileText } from 'lucide-react';

const capitalizeWords = (str) => {
    if (!str) return '';
    return str.split(' ').map(word =>
        word.length > 0 ? word.charAt(0).toLocaleUpperCase('tr') + word.slice(1).toLocaleLowerCase('tr') : ''
    ).join(' ');
};

const AddCustomerModal = ({ isOpen, onClose, onAdd, t }) => {
    const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', notes: '' });
    const [submitting, setSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await onAdd(form);
            setForm({ firstName: '', lastName: '', email: '', phone: '', notes: '' });
        } catch {
            // Error handled in parent
        } finally {
            setSubmitting(false);
        }
    };

    const inputStyle = {
        width: '100%',
        height: '48px',
        background: 'var(--bg-main)',
        border: '1px solid var(--border)',
        color: 'var(--text-main)',
        borderRadius: '12px',
        outline: 'none',
        paddingLeft: '42px',
        fontSize: '0.9rem',
        transition: 'all 0.2s',
    };

    const labelStyle = {
        fontSize: '0.75rem',
        fontWeight: 700,
        color: 'var(--text-muted)',
        marginBottom: '6px',
        display: 'block',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
    };

    const iconStyle = {
        position: 'absolute',
        left: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: 'var(--text-muted)',
        pointerEvents: 'none'
    };

    return (
        <div className="profile-modal-overlay" onClick={onClose} style={{ zIndex: 9999 }}>
            <div className="card glass shadow-2xl" style={{ maxWidth: '520px', width: '100%', padding: 0, overflow: 'hidden', border: 'none', borderRadius: '24px' }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{
                    padding: '1.75rem 2rem',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: 'linear-gradient(to bottom, rgba(var(--primary-rgb), 0.03), transparent)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '44px', height: '44px', borderRadius: '14px',
                            background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <UserPlus size={22} />
                        </div>
                        <div>
                            <h2 style={{ fontWeight: 800, fontSize: '1.15rem', margin: 0 }}>{t('customers.addCustomer')}</h2>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: 0 }}>Yeni müşteri bilgilerini girin</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'rgba(0,0,0,0.05)',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '10px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-main)',
                        transition: 'all 0.2s',
                        outline: 'none'
                    }} 
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                    onMouseOut={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                    >
                        <X size={20} strokeWidth={2.5} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} style={{ padding: '2rem' }}>
                    <style>{`@media (max-width: 480px) { .add-customer-name-grid { grid-template-columns: 1fr !important; } .add-customer-form { padding: 1.25rem !important; } }`}</style>
                    <div className="add-customer-name-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                        <div>
                            <label style={labelStyle}>Ad *</label>
                            <div style={{ position: 'relative' }}>
                                <User size={16} style={iconStyle} />
                                <input
                                    required placeholder="Ad"
                                    value={form.firstName}
                                    onChange={e => setForm({ ...form, firstName: capitalizeWords(e.target.value) })}
                                    style={inputStyle}
                                />
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>Soyad *</label>
                            <div style={{ position: 'relative' }}>
                                <User size={16} style={iconStyle} />
                                <input
                                    required placeholder="Soyad"
                                    value={form.lastName}
                                    onChange={e => setForm({ ...form, lastName: capitalizeWords(e.target.value) })}
                                    style={inputStyle}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={labelStyle}>E-posta *</label>
                        <div style={{ position: 'relative' }}>
                            <Mail size={16} style={iconStyle} />
                            <input
                                type="email" required placeholder="ornek@email.com"
                                value={form.email}
                                onChange={e => setForm({ ...form, email: e.target.value.toLowerCase() })}
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={labelStyle}>Telefon *</label>
                        <div style={{ position: 'relative' }}>
                            <Phone size={16} style={iconStyle} />
                            <input
                                type="tel" required placeholder="05XX XXX XX XX"
                                value={form.phone}
                                onChange={e => {
                                    let val = e.target.value.replace(/[^0-9]/g, '');
                                    setForm({ ...form, phone: val });
                                }}
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '2.5rem' }}>
                        <label style={labelStyle}>Not</label>
                        <div style={{ position: 'relative' }}>
                            <FileText size={16} style={{ ...iconStyle, top: '18px', transform: 'none' }} />
                            <textarea
                                placeholder="Müşteri hakkında not ekleyin..."
                                value={form.notes}
                                onChange={e => setForm({ ...form, notes: e.target.value })}
                                rows={3}
                                style={{
                                    ...inputStyle,
                                    height: 'auto',
                                    paddingTop: '12px',
                                    paddingBottom: '12px',
                                    resize: 'vertical',
                                    minHeight: '80px',
                                    fontFamily: 'inherit'
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button type="button" onClick={onClose} className="btn-secondary" style={{
                            flex: 1, height: '52px', borderRadius: '14px', fontWeight: 700, fontSize: '0.95rem'
                        }}>
                            İptal
                        </button>
                        <button type="submit" className="btn-primary" disabled={submitting} style={{
                            flex: 1, height: '52px', borderRadius: '14px', fontWeight: 700, fontSize: '0.95rem',
                            opacity: submitting ? 0.7 : 1,
                            boxShadow: '0 8px 20px rgba(var(--primary-rgb), 0.2)'
                        }}>
                            {submitting ? 'Ekleniyor...' : 'Müşteri Ekle'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddCustomerModal;

